import { useRef, useState } from "react";

export interface MiniChartPoint {
  /** Epoch seconds. */
  x: number;
  /** Numeric value plotted. */
  y: number;
  /** Display date for the tooltip (YYYY-MM-DD). */
  date: string;
  /** Display value for the tooltip (the raw actual string). */
  label: string;
}

const WIDTH = 80;
const HEIGHT = 24;
const PAD = 2;

/** Sparkline of a data category's historical actual values. Hover shows the
 *  nearest point's date and value. Stroke uses the theme accent. */
export function MiniChart({ points }: { points: MiniChartPoint[] }) {
  const ref = useRef<HTMLDivElement>(null);
  const [tip, setTip] = useState<{ left: number; text: string } | null>(null);

  if (points.length === 0) return null;

  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const spanX = maxX - minX || 1;
  const spanY = maxY - minY || 1;
  const px = (x: number) => PAD + ((x - minX) / spanX) * (WIDTH - 2 * PAD);
  const py = (y: number) => HEIGHT - PAD - ((y - minY) / spanY) * (HEIGHT - 2 * PAD);
  const path = points
    .map((p, i) => `${i ? "L" : "M"}${px(p.x).toFixed(1)},${py(p.y).toFixed(1)}`)
    .join(" ");

  const onMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const xPx = Math.min(Math.max(e.clientX - rect.left, 0), rect.width);
    const target = minX + (xPx / (rect.width || 1)) * (maxX - minX);
    let nearest = points[0];
    let best = Infinity;
    for (const p of points) {
      const d = Math.abs(p.x - target);
      if (d < best) {
        best = d;
        nearest = p;
      }
    }
    setTip({ left: xPx, text: `${nearest.date} · ${nearest.label}` });
  };

  return (
    <div
      className="mini-chart"
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={() => setTip(null)}
    >
      <svg width={WIDTH} height={HEIGHT} viewBox={`0 0 ${WIDTH} ${HEIGHT}`}>
        <path d={path} fill="none" stroke="var(--accent)" strokeWidth={1.5} />
      </svg>
      {tip && (
        <div className="mini-chart-tooltip" style={{ left: tip.left }}>
          {tip.text}
        </div>
      )}
    </div>
  );
}

export default MiniChart;
